import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  BookOpen, Target, Award, ChevronRight,
  Play, Clock, CheckCircle, Brain, Zap, Trophy, Flame, Star,
  BarChart3, Sparkles, ArrowRight, Bell, Settings, RefreshCw,
  Code, User, Wrench, X
} from 'lucide-react';
import Loading from '../components/common/Loading';
import DailyLearningPlannerWidget from '../components/planner/DailyLearningPlannerWidget';
import MilestoneTracker from '../components/planner/MilestoneTracker';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const selectContinueModule = (modules = []) => {
  if (!Array.isArray(modules) || modules.length === 0) {
    return null;
  }

  const normalizeModule = (module) => ({
    ...module,
    progress: Math.max(0, Math.min(100, Number(module?.progress || 0))),
    estimated_hours: Number(module?.estimated_hours ?? module?.estimatedHours ?? 25),
    completed_hours: Number(module?.completed_hours ?? module?.completedHours ?? 0),
    skills: Array.isArray(module?.skills) ? module.skills : [],
  });

  const priorityChecks = [
    (module) => module?.status === 'in_progress',
    (module) => module?.status === 'current',
    (module) => (
      ['unlocked', 'available', 'not_started'].includes(module?.status)
      || (!['completed', 'locked'].includes(module?.status) && Number(module?.progress || 0) === 0)
    ),
  ];

  for (const matches of priorityChecks) {
    const selected = modules.find(matches);
    if (selected) {
      return normalizeModule(selected);
    }
  }

  return null;
};

const normalizeExplainerRoleValue = (value) => String(value || '').trim();

const normalizeExplainerRoleKey = (value) => normalizeExplainerRoleValue(value)
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const resolveExplainerRole = ({ role, profile, userProfile, user }) => {
  const selectedRoleId = normalizeExplainerRoleValue(role?.selected_role_id);
  const selectedRoleTitle = normalizeExplainerRoleValue(role?.title);
  if (selectedRoleId && selectedRoleTitle) {
    return {
      title: selectedRoleTitle,
      source: 'selected_recommended_role',
    };
  }

  const customRole = normalizeExplainerRoleValue(role?.custom_role);
  if (customRole) {
    return {
      title: customRole,
      source: 'custom_role',
    };
  }

  const onboardingCareerGoal = normalizeExplainerRoleValue(
    profile?.career_goal || userProfile?.career_goal || user?.career_goal
  );
  if (onboardingCareerGoal) {
    return {
      title: onboardingCareerGoal,
      source: 'career_goal',
    };
  }

  return {
    title: '',
    source: null,
  };
};

const Dashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const [greeting, setGreeting] = useState('');

  // AI Explainer
  const [explainerTab, setExplainerTab] = useState('video');
  const [explainerModel, setExplainerModel] = useState('mistral');
  const [explainerJobs, setExplainerJobs] = useState({ video: null, audio: null });
  const [explainerStatuses, setExplainerStatuses] = useState({ video: null, audio: null });
  const explainerJobsRef = useRef({ video: null, audio: null });
  const [explainerModalOpen, setExplainerModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const explainerPollRef = useRef(null);

  const PIPELINE_URL = 'http://178.105.145.38:8002';

  const dashboardDataRef = useRef(null);

  const { scrollY } = useScroll();
  const cardsY = useTransform(scrollY, [0, 500], [0, -50]);

  useEffect(() => {
    dashboardDataRef.current = dashboardData;
  }, [dashboardData]);

  useEffect(() => {
    const updateGreeting = () => {
      const hour = new Date().getHours();
      if (hour < 12) setGreeting('Good Morning');
      else if (hour < 17) setGreeting('Good Afternoon');
      else if (hour < 21) setGreeting('Good Evening');
      else setGreeting('Good Night');
    };

    updateGreeting();
    const timer = setInterval(updateGreeting, 60000);
    return () => clearInterval(timer);
  }, []);

  const fetchDashboardData = useCallback(async (options = {}) => {
    const { silent = false, showSuccess = false } = options;
    const hasDashboardData = Boolean(dashboardDataRef.current);
    try {
      if (!silent) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }
      if (!hasDashboardData) {
        setError(null);
      }
      setRefreshError(null);
      const data = await dashboardAPI.getDashboard();
      setDashboardData(data);
      setError(null);
      if (showSuccess) {
        toast.success('Dashboard refreshed');
      }
    } catch (fetchError) {
      console.error('Dashboard error:', fetchError);
      const message = fetchError.message || 'Failed to load dashboard';
      if (silent && hasDashboardData) {
        setRefreshError(message);
      } else {
        setError(message);
        toast.error('Failed to load dashboard');
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    const handleFocus = () => fetchDashboardData({ silent: true });
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchDashboardData({ silent: true });
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    const poller = setInterval(() => {
      fetchDashboardData({ silent: true });
    }, 60000);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(poller);
    };
  }, [fetchDashboardData]);

  const FloatingElement = ({ children, className, delay = 0 }) => (
    <motion.div
      className={`absolute ${className}`}
      animate={{
        y: [-20, 20, -20],
        rotate: [-5, 5, -5],
        opacity: [0.1, 0.3, 0.1]
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut',
        delay
      }}
    >
      {children}
    </motion.div>
  );

  const getScoreColor = (score) => {
    if (score >= 80) return 'from-green-500 to-emerald-500';
    if (score >= 50) return 'from-yellow-500 to-orange-500';
    return 'from-rose-500 to-pink-500';
  };

  const getScoreBadge = (score) => {
    if (score >= 80) return 'text-green-300 bg-green-500/15 border-green-500/30';
    if (score >= 50) return 'text-yellow-300 bg-yellow-500/15 border-yellow-500/30';
    return 'text-rose-300 bg-rose-500/15 border-rose-500/30';
  };

  const safeDashboardData = useMemo(() => (
    dashboardData || {
      user_profile: {
        name: user?.name || 'Learner',
        email: user?.email || '',
        career_goal: user?.career_goal || null,
        current_streak: 0,
        preferred_language: user?.preferred_language || null,
        onboarding_completed: Boolean(user?.onboarding_completed),
      },
      progress: {
        overall_percentage: 0,
        average_score: null,
        total_modules: 0,
        assessment_attempts: 0,
        completed_modules: 0,
        in_progress_modules: 0,
        locked_modules: 0,
      },
      current_module: null,
      quick_stats: {
        assessments_passed: 0,
        learning_streak: 0,
        next_milestone: null,
      },
      ai_insights: {
        performance_trend: null,
        recommended_focus: null,
        learning_velocity: null,
        next_best_action: null,
        next_milestone: null,
      },
      notifications: [],
      roadmaps: [],
      active_roadmap: null,
      recent_activity: [],
      first_assessment_score: null,
      weak_skills: [],
      career_goal_definition: null,
      dashboard_summary: {
        profile: {
          name: user?.name || 'Learner',
          email: user?.email || '',
          experience_level: user?.experience_level || null,
          preferred_language: user?.preferred_language || null,
          skills_count: 0,
          has_profile: false,
        },
        role: {
          title: user?.career_goal || null,
          type: null,
          description: null,
        },
        readiness: {
          score: 0,
          components: [],
          assessment_average: null,
          role_coverage: null,
        },
        first_assessment_score: null,
        career_goal_definition: null,
        skill_scores: [],
        weak_skills: [],
        empty_state: {
          has_profile: false,
          has_role: Boolean(user?.career_goal),
          has_skills: false,
          has_role_framework: false,
        },
      },
    }
  ), [dashboardData, user]);

  const { user_profile, progress, current_module, quick_stats, ai_insights, notifications, roadmaps, active_roadmap, recent_activity, dashboard_summary } = safeDashboardData;
  const summary = dashboard_summary || {};
  const profile = summary.profile || {};
  const role = summary.role || {};
  const readiness = summary.readiness || {};
  const skillScores = Array.isArray(summary.skill_scores) ? summary.skill_scores : [];
  const weakSkills = Array.isArray(summary.weak_skills) && summary.weak_skills.length > 0
    ? summary.weak_skills
    : (Array.isArray(safeDashboardData.weak_skills) ? safeDashboardData.weak_skills : []);
  const emptyState = summary.empty_state || {};
  const activeRoadmapModules = Array.isArray(active_roadmap?.modules)
    ? active_roadmap.modules
    : (
      Array.isArray(roadmaps)
        ? (roadmaps.find((roadmap) => roadmap?.is_active)?.modules || roadmaps[0]?.modules || [])
        : []
    );
  const currentModule = selectContinueModule(activeRoadmapModules) || current_module;
  const hasRoadmap = !!active_roadmap || (progress?.total_modules || 0) > 0 || activeRoadmapModules.length > 0;
  const hasCurrentModule = Boolean(currentModule) && ((progress?.total_modules || 0) > 0 || activeRoadmapModules.length > 0);
  const recentActivity = Array.isArray(recent_activity) ? recent_activity : [];
  const profileName = profile?.name || user_profile?.name || user?.name || 'Learner';
  const roleTitle = role?.title || user_profile?.career_goal || user?.career_goal || 'Role not selected yet';
  const explainerRole = useMemo(
    () => resolveExplainerRole({
      role,
      profile,
      userProfile: user_profile,
      user,
    }),
    [role, profile, user_profile, user]
  );
  const explainerRoleTitle = explainerRole.title;
  const hasExplainerRole = Boolean(explainerRoleTitle);
  const readinessScore = readiness?.score || 0;
  const readinessComponents = Array.isArray(readiness?.components) ? readiness.components : [];
  const topSkillScores = skillScores.slice(0, 5);
  const topWeakSkills = weakSkills.slice(0, 4);
  const firstAssessmentScore = summary?.first_assessment_score ?? safeDashboardData?.first_assessment_score ?? null;
  const firstAssessmentDisplay = firstAssessmentScore === null || firstAssessmentScore === undefined
    ? 'No assessment attempted yet'
    : `${firstAssessmentScore}%`;
  const careerGoalDefinition = summary?.career_goal_definition
    || safeDashboardData?.career_goal_definition
    || role?.description
    || (role?.type === 'custom'
      ? 'Custom learner-defined target role selected by the user.'
      : 'Role definition not available yet.');
  const performanceDisplay = ai_insights?.performance_trend || (progress?.assessment_attempts > 0
    ? ((progress?.average_score ?? 0) >= 70 ? 'improving' : 'needs attention')
    : 'not enough data yet');
  const paceDisplay = ai_insights?.learning_velocity || (
    progress?.assessment_attempts > 0
      ? 'active'
      : hasRoadmap
        ? 'getting started'
        : 'not started yet'
  );
  const nextBestActionDisplay = ai_insights?.next_best_action || (
    !user_profile?.onboarding_completed
      ? 'Complete onboarding'
      : currentModule?.title
        ? `Resume '${currentModule.title}'`
        : emptyState?.has_role
          ? (progress?.assessment_attempts > 0 ? 'Open your roadmap' : 'Take your first assessment')
          : 'Choose your target role'
  );
  const showStatePanel = Boolean(error) || isLoading || Boolean(refreshError) || isRefreshing || !dashboardData;
  const infoChips = [
    profile?.email || user_profile?.email || user?.email,
    profile?.phone,
    profile?.experience_level,
    profile?.preferred_language,
  ].filter(Boolean);
  const explainerEmptyMessage = hasExplainerRole
    ? `No ${explainerTab} explainer for ${explainerRoleTitle} yet`
    : 'Complete onboarding or select a role to generate personalized explainer content.';
  const explainerHintMessage = hasExplainerRole
    ? `Generate one for ${explainerRoleTitle}`
    : 'Personalized explainer content will appear here once a role is available.';
  const explainerStorageKey = useMemo(() => {
    const userKey = user?.id || user?.email || 'guest';
    const roleKey = normalizeExplainerRoleKey(explainerRoleTitle);
    return roleKey
      ? `aivion_explainer_${userKey}_${roleKey}`
      : `aivion_explainer_${userKey}_no_role`;
  }, [user, explainerRoleTitle]);

  const handleGenerate = async () => {
    if (isGenerating || !hasExplainerRole) return;

    const skillGapData = {
      role: explainerRoleTitle,
      readiness_score: readinessScore || 0,
      weak_skills: topWeakSkills.slice(0, 3).map((s) => s.name),
      skill_scores: Object.fromEntries(skillScores.slice(0, 7).map((s) => [s.name, s.score])),
      model: explainerModel,
    };

    const currentTab = explainerTab;
    setIsGenerating(true);
    setExplainerStatuses((prev) => ({ ...prev, [currentTab]: 'processing' }));
    if (explainerPollRef.current) clearInterval(explainerPollRef.current);

    try {
      const res = await fetch(`${PIPELINE_URL}/pipeline/${currentTab}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(skillGapData),
      });
      if (!res.ok) throw new Error('Request failed');
      const { job_id } = await res.json();

      explainerPollRef.current = setInterval(async () => {
        try {
          const s = await fetch(`${PIPELINE_URL}/pipeline/${currentTab}/status/${job_id}`);
          const { status, error } = await s.json();
          if (status === 'done') {
            clearInterval(explainerPollRef.current);
            const updated = { ...explainerJobsRef.current, [currentTab]: job_id };
            explainerJobsRef.current = updated;
            setExplainerJobs(updated);
            setExplainerStatuses((prev) => ({ ...prev, [currentTab]: 'done' }));
            setIsGenerating(false);
            localStorage.setItem(explainerStorageKey, JSON.stringify({
              roleTitle: explainerRoleTitle,
              jobs: updated,
            }));
            toast.success('Your explainer is ready!');
          } else if (status === 'failed') {
            clearInterval(explainerPollRef.current);
            setExplainerStatuses((prev) => ({ ...prev, [currentTab]: 'failed' }));
            setIsGenerating(false);
            toast.error(error || 'Generation failed.');
          }
        } catch {
          clearInterval(explainerPollRef.current);
          setIsGenerating(false);
        }
      }, 15000);
    } catch {
      setExplainerStatuses((prev) => ({ ...prev, [currentTab]: 'failed' }));
      setIsGenerating(false);
      toast.error('Could not reach the pipeline server.');
    }
  };

  useEffect(() => () => { if (explainerPollRef.current) clearInterval(explainerPollRef.current); }, []);

  useEffect(() => {
    if (!hasExplainerRole) {
      setExplainerJobs({ video: null, audio: null });
      explainerJobsRef.current = { video: null, audio: null };
      setExplainerStatuses({ video: null, audio: null });
      setExplainerModalOpen(false);
      return;
    }

    const saved = localStorage.getItem(explainerStorageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const jobs = parsed?.jobs || parsed;
        setExplainerJobs(jobs);
        explainerJobsRef.current = jobs;
        setExplainerStatuses({
          video: jobs.video ? 'done' : null,
          audio: jobs.audio ? 'done' : null,
        });
      } catch {
        setExplainerJobs({ video: null, audio: null });
        explainerJobsRef.current = { video: null, audio: null };
        setExplainerStatuses({ video: null, audio: null });
      }
    } else {
      setExplainerJobs({ video: null, audio: null });
      explainerJobsRef.current = { video: null, audio: null };
      setExplainerStatuses({ video: null, audio: null });
    }
  }, [explainerStorageKey, hasExplainerRole]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <FloatingElement className="top-20 left-10 text-purple-400" delay={0}>
          <Brain size={60} />
        </FloatingElement>
        <FloatingElement className="top-40 right-20 text-blue-400" delay={2}>
          <Code size={50} />
        </FloatingElement>
        <FloatingElement className="bottom-40 left-20 text-pink-400" delay={4}>
          <Star size={70} />
        </FloatingElement>
        <FloatingElement className="bottom-20 right-10 text-green-400" delay={6}>
          <Trophy size={55} />
        </FloatingElement>
        <FloatingElement className="top-1/2 left-1/3 text-yellow-400" delay={3}>
          <Sparkles size={45} />
        </FloatingElement>
      </div>

      <motion.div
        className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500 rounded-full blur-3xl opacity-20"
        animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-56 h-56 bg-pink-500 rounded-full blur-3xl opacity-20"
        animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.2, 0.3] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
      />
      <motion.div
        className="absolute top-1/2 right-1/3 w-48 h-48 bg-blue-500 rounded-full blur-3xl opacity-15"
        animate={{ scale: [1, 1.3, 1], opacity: [0.15, 0.25, 0.15] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
      />

      <div className="pt-20 relative z-10">
        <motion.section className="py-12 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-8"
            >
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
                {greeting}, {profileName}!
              </h1>
              <p className="text-xl text-gray-300">
                Ready to continue your <span className="text-purple-400 font-semibold">AI-powered</span> learning journey?
              </p>
              <p className="text-gray-400 mt-2">
                Targeting <span className="text-pink-400">{roleTitle}</span>
                {role?.type ? <span className="text-gray-500"> • {role.type}</span> : null}
              </p>
              {infoChips.length > 0 && (
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {infoChips.slice(0, 4).map((chip) => (
                    <span key={chip} className="px-3 py-1 bg-slate-800/40 border border-slate-700/50 rounded-full text-sm text-gray-300 backdrop-blur-sm">
                      {chip}
                    </span>
                  ))}
                </div>
              )}
            </motion.div>

            {showStatePanel && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-3xl mx-auto bg-slate-800/30 backdrop-blur-xl rounded-2xl p-5 border border-slate-700/50 mb-8"
              >
                {isLoading && !dashboardData ? (
                  <Loading fullScreen={false} text="Loading your personalized dashboard..." />
                ) : (
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-left">
                    <div>
                      <p className={`text-lg font-semibold ${error ? 'text-red-400' : 'text-white'}`}>
                        {error ? 'Dashboard unavailable right now' : isRefreshing ? 'Refreshing dashboard data...' : refreshError ? 'Showing your last loaded dashboard' : 'Preparing your dashboard'}
                      </p>
                      <p className="text-gray-400 text-sm mt-1">
                        {error || refreshError || 'We are syncing your latest profile, role, and readiness data.'}
                      </p>
                    </div>
                    {(error || refreshError) && (
                      <button
                        onClick={() => fetchDashboardData({ showSuccess: true })}
                        className="px-5 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:scale-105 transition-transform font-semibold"
                      >
                        <RefreshCw className="w-5 h-5 inline mr-2" />
                        Retry
                      </button>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </motion.section>

        <motion.main className="max-w-7xl mx-auto px-6 pb-16" style={{ y: cardsY }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {[
              {
                label: 'Readiness',
                value: readinessScore,
                unit: '%',
                icon: Award,
                color: 'from-purple-600 to-pink-600',
                bgColor: 'bg-purple-500/20'
              },
              {
                label: 'Skills',
                value: profile?.skills_count || topSkillScores.length || 0,
                icon: BarChart3,
                color: 'from-blue-600 to-cyan-600',
                bgColor: 'bg-blue-500/20'
              },
              {
                label: 'Assessments',
                value: quick_stats?.assessments_passed || 0,
                total: progress?.assessment_attempts || 0,
                icon: Target,
                color: 'from-green-600 to-emerald-600',
                bgColor: 'bg-green-500/20'
              },
              {
                label: 'Streak',
                value: quick_stats?.learning_streak || user_profile?.current_streak || 0,
                unit: 'days',
                icon: Flame,
                color: 'from-orange-600 to-red-600',
                bgColor: 'bg-orange-500/20'
              }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5, boxShadow: '0 20px 25px -5px rgba(139, 92, 246, 0.3)' }}
                className={`p-6 bg-slate-800/30 backdrop-blur-xl rounded-2xl border border-slate-700/50 ${stat.bgColor} relative overflow-hidden cursor-pointer`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-2">{stat.label}</p>
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold text-white">{stat.value}</span>
                      {stat.total ? <span className="text-gray-400 text-sm ml-1">/{stat.total}</span> : null}
                      {stat.unit ? <span className="text-gray-400 text-sm ml-1">{stat.unit}</span> : null}
                    </div>
                  </div>
                  <motion.div
                    className={`w-14 h-14 bg-gradient-to-r ${stat.color} rounded-xl flex items-center justify-center`}
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    <stat.icon className="w-7 h-7 text-white" />
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <DailyLearningPlannerWidget />

              {hasCurrentModule ? (
                <motion.section
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="bg-slate-800/30 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50 relative overflow-hidden"
                >
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-3xl font-bold text-white flex items-center">
                        <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
                          <Sparkles className="w-7 h-7 text-purple-400 mr-3" />
                        </motion.div>
                        Continue Learning
                      </h2>
                      <div className="flex items-center text-purple-400 text-sm font-medium">
                        <Clock className="w-4 h-4 mr-1" />
                        {currentModule.estimated_hours}h estimated
                      </div>
                    </div>

                    <h3 className="text-2xl font-semibold text-white mb-3">{currentModule.title}</h3>
                    <p className="text-gray-400 mb-6">{currentModule.description}</p>

                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-gray-400">Module Progress</span>
                        <span className="text-purple-400 font-bold text-lg">{currentModule.progress}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-4 overflow-hidden">
                        <motion.div
                          className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${currentModule.progress}%` }}
                          transition={{ duration: 1.5, ease: 'easeOut' }}
                        />
                      </div>
                    </div>

                    {currentModule.skills && currentModule.skills.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-6">
                        {currentModule.skills.slice(0, 6).map((skill, index) => (
                          <motion.span
                            key={skill}
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.1 }}
                            className="px-4 py-2 bg-purple-500/20 text-purple-300 rounded-full text-sm font-medium"
                          >
                            {skill}
                          </motion.span>
                        ))}
                      </div>
                    )}

                    <Link to={`/lesson/${currentModule.id}${active_roadmap?.id ? `?roadmapId=${active_roadmap.id}` : ''}`}>
                      <motion.button
                        whileHover={{ scale: 1.05, boxShadow: '0 20px 25px -5px rgba(139, 92, 246, 0.4)' }}
                        whileTap={{ scale: 0.95 }}
                        className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-xl transition-all flex items-center justify-center text-lg"
                      >
                        <Play className="w-5 h-5 mr-2" />
                        Continue Module
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </motion.button>
                    </Link>
                  </div>
                </motion.section>
              ) : hasRoadmap ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-3xl p-8 text-white text-center"
                >
                  <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                    <CheckCircle className="w-20 h-20 mx-auto mb-4" />
                  </motion.div>
                  <h2 className="text-3xl font-bold mb-3">All Caught Up!</h2>
                  <p className="text-green-100 text-lg mb-6">Congratulations! You&apos;ve completed all modules.</p>
                  <Link to="/roadmap" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-green-600 font-bold rounded-xl hover:scale-105 transition-all">
                    View Achievement
                    <Trophy className="w-5 h-5" />
                  </Link>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-br from-blue-600 to-cyan-700 rounded-3xl p-8 text-white text-center"
                >
                  <h2 className="text-3xl font-bold mb-3">Set Up Your Learning Path</h2>
                  <p className="text-blue-100 text-lg mb-6">Complete onboarding to generate your personalized roadmap.</p>
                  <Link to="/onboarding" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-700 font-bold rounded-xl hover:scale-105 transition-all">
                    Start Onboarding
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </motion.div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50"
                >
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                    <BarChart3 className="w-5 h-5 text-purple-400 mr-2" />
                    Progress & Readiness
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-400">Roadmap Progress</span>
                        <span className="text-white font-bold">{progress?.overall_percentage || 0}%</span>
                      </div>
                      <div className="bg-slate-700 rounded-full h-2">
                        <motion.div
                          className="h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${progress?.overall_percentage || 0}%` }}
                          transition={{ duration: 1 }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-400">Readiness Score</span>
                        <span className="text-white font-bold">{readinessScore}%</span>
                      </div>
                      <div className="bg-slate-700 rounded-full h-2">
                        <motion.div
                          className={`h-2 bg-gradient-to-r ${getScoreColor(readinessScore)} rounded-full`}
                          initial={{ width: 0 }}
                          animate={{ width: `${readinessScore}%` }}
                          transition={{ duration: 1 }}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 pt-2">
                      <div className="bg-slate-700/30 rounded-lg p-3">
                        <p className="text-gray-400 text-xs">Assessment Avg</p>
                        <p className="text-white font-bold">{readiness?.assessment_average ?? 0}%</p>
                      </div>
                      <div className="bg-slate-700/30 rounded-lg p-3">
                        <p className="text-gray-400 text-xs">Role Coverage</p>
                        <p className="text-white font-bold">{readiness?.role_coverage ?? 0}%</p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* AI Explainer row 1, col 2 */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 flex flex-col"
                >
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                    <Sparkles className="w-5 h-5 text-purple-400 mr-2" />
                    AI Explainer
                  </h3>

                  {/* Format tabs */}
                  <div className="flex bg-slate-700/40 rounded-lg p-1 mb-4">
                    {['video', 'audio'].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setExplainerTab(tab)}
                        disabled={isGenerating}
                        className={`flex-1 py-1.5 rounded-md text-sm font-medium capitalize transition-all ${
                          explainerTab === tab
                            ? 'bg-purple-600 text-white shadow-sm'
                            : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        {tab === 'video' ? '🎬 Video' : '🎧 Audio'}
                      </button>
                    ))}
                  </div>

                  {/* Model selector */}
                  <select
                    value={explainerModel}
                    onChange={(e) => setExplainerModel(e.target.value)}
                    disabled={isGenerating}
                    className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg px-3 py-2 text-white text-sm mb-4 focus:outline-none focus:border-purple-500/50"
                  >
                    <option value="mistral">Mistral 7B</option>
                    <option value="llama3.1">Llama 3.1 8B</option>
                    <option value="qwen2.5">Qwen 2.5 7B</option>
                  </select>

                  {/* Preview area */}
                  <div className="flex-1 mb-4">
                    {explainerStatuses[explainerTab] === 'done' && explainerJobs[explainerTab] ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        onClick={() => setExplainerModalOpen(true)}
                        className="relative bg-slate-900/60 rounded-xl border border-purple-500/30 overflow-hidden cursor-pointer group"
                      >
                        <div className="aspect-video flex flex-col items-center justify-center gap-2">
                          <div className={`w-14 h-14 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform ${
                            explainerTab === 'video' ? 'bg-purple-600/80' : 'bg-green-600/80'
                          }`}>
                            <Play className="w-6 h-6 text-white ml-1" />
                          </div>
                          <p className="text-gray-300 text-sm">
                            {explainerTab === 'video' ? 'Click to watch' : 'Click to listen'}
                          </p>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/80 to-transparent p-3">
                          <p className="text-white text-xs font-medium truncate">Personalised for {explainerRoleTitle}</p>
                        </div>
                      </motion.div>
                    ) : explainerStatuses[explainerTab] === 'processing' ? (
                      <div className="aspect-video flex flex-col items-center justify-center bg-slate-900/40 rounded-xl border border-slate-700/40 gap-3">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                          className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full"
                        />
                        <div className="text-center">
                          <p className="text-gray-300 text-sm">Generating {explainerTab}…</p>
                          <p className="text-gray-500 text-xs mt-1">Takes 3–10 minutes</p>
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-video flex flex-col items-center justify-center bg-slate-900/40 rounded-xl border border-dashed border-slate-700/60 gap-2">
                        <Sparkles className="w-8 h-8 text-slate-500" />
                        <p className="text-gray-400 text-sm text-center px-4">{explainerEmptyMessage}</p>
                        <p className="text-gray-500 text-xs text-center px-4">{explainerHintMessage}</p>
                      </div>
                    )}
                  </div>

                  {/* Generate button */}
                  <motion.button
                    whileHover={{ scale: isGenerating ? 1 : 1.02 }}
                    whileTap={{ scale: isGenerating ? 1 : 0.98 }}
                    onClick={handleGenerate}
                    disabled={isGenerating || !hasExplainerRole}
                    className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                      isGenerating || !hasExplainerRole
                        ? 'bg-slate-700/50 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
                    }`}
                  >
                    {isGenerating ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full"
                        />
                        Generating…
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Generate {explainerTab === 'video' ? 'Video' : 'Audio'}
                      </>
                    )}
                  </motion.button>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50"
                >
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                    <User className="w-5 h-5 text-blue-400 mr-2" />
                    Profile & Role
                  </h3>
                  <div className="space-y-3">
                    <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
                      <p className="text-blue-400 text-xs mb-1">Active Role</p>
                      <p className="text-white font-semibold">{roleTitle}</p>
                      <p className="text-gray-300 text-xs mt-2 leading-relaxed">{careerGoalDefinition}</p>
                    </div>
                    <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/20">
                      <p className="text-green-400 text-xs mb-1">Profile Status</p>
                      <p className="text-white font-semibold">
                        {emptyState?.has_profile ? 'Profile available' : 'Profile incomplete'}
                      </p>
                    </div>
                    <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/20">
                      <p className="text-purple-400 text-xs mb-1">First Assessment</p>
                      <p className="text-white font-semibold">{firstAssessmentDisplay}</p>
                    </div>
                    <div className="text-sm text-gray-400 pt-1">
                      <p>{profile?.email || user_profile?.email || user?.email || 'No email available'}</p>
                      <p className="capitalize">{profile?.experience_level || user?.experience_level || 'No experience data yet'}</p>
                    </div>
                  </div>
                </motion.div>

                {/* 🆕 LEARNING PROGRESS WIDGET */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50"
                >
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                    <Clock className="w-5 h-5 text-green-400 mr-2" />
                    Learning Activity
                  </h3>
                  <div className="space-y-3">
                    {progress?.time_spent_breakdown ? (
                      <>
                        <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/20">
                          <p className="text-green-400 text-xs mb-1">Total Time Spent</p>
                          <p className="text-white font-bold text-lg">
                            {Math.floor((progress.time_spent_breakdown.total_minutes || 0) / 60)}h {(progress.time_spent_breakdown.total_minutes || 0) % 60}m
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-blue-500/10 rounded p-2 border border-blue-500/20">
                            <p className="text-blue-400">📹 Video</p>
                            <p className="text-white font-semibold">{Math.floor((progress.time_spent_breakdown.video_minutes || 0) / 60)}h</p>
                          </div>
                          <div className="bg-purple-500/10 rounded p-2 border border-purple-500/20">
                            <p className="text-purple-400">📖 Reading</p>
                            <p className="text-white font-semibold">{Math.floor((progress.time_spent_breakdown.reading_minutes || 0) / 60)}h</p>
                          </div>
                          <div className="bg-orange-500/10 rounded p-2 border border-orange-500/20">
                            <p className="text-orange-400">⚙️ Practice</p>
                            <p className="text-white font-semibold">{Math.floor((progress.time_spent_breakdown.practice_minutes || 0) / 60)}h</p>
                          </div>
                          <div className="bg-pink-500/10 rounded p-2 border border-pink-500/20">
                            <p className="text-pink-400">🎓 Courses</p>
                            <p className="text-white font-semibold">{Math.floor((progress.time_spent_breakdown.course_minutes || 0) / 60)}h</p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-gray-400 text-sm text-center py-6">
                        <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Start learning to track your time</p>
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* 🆕 SKILL PROGRESS WIDGET */}
                {progress?.skill_progress && Object.keys(progress.skill_progress).length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50"
                  >
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                      <Brain className="w-5 h-5 text-indigo-400 mr-2" />
                      Skill Progress
                    </h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {Object.entries(progress.skill_progress).slice(0, 5).map(([key, skill]) => (
                        <div key={key} className="bg-slate-700/25 rounded-lg p-3 border border-slate-700/40">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-white font-medium text-sm">{skill.skill}</span>
                            <span className={`text-xs px-2 py-1 rounded ${
                              skill.status === 'proficient' ? 'bg-green-500/20 text-green-400' :
                              skill.status === 'practicing' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {skill.status}
                            </span>
                          </div>
                          <div className="bg-slate-700 rounded-full h-1.5">
                            <motion.div
                              className={`h-1.5 rounded-full ${
                                skill.status === 'proficient' ? 'bg-green-500' :
                                skill.status === 'practicing' ? 'bg-yellow-500' :
                                'bg-blue-500'
                              }`}
                              initial={{ width: 0 }}
                              animate={{ width: `${skill.progress_percentage || 0}%` }}
                              transition={{ duration: 0.5 }}
                            />
                          </div>
                          <p className="text-gray-400 text-xs mt-1">{skill.progress_percentage || 0}%</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>

              <motion.section
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-slate-800/30 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-white flex items-center">
                    <Brain className="w-6 h-6 text-purple-400 mr-3" />
                    Skill Scores
                  </h3>
                  {topSkillScores.length > 0 ? (
                    <span className={`px-3 py-1 rounded-full border text-sm font-medium ${getScoreBadge(readinessScore)}`}>
                      {readinessScore}% readiness
                    </span>
                  ) : null}
                </div>

                {topSkillScores.length > 0 ? (
                  <div className="space-y-4">
                    {topSkillScores.map((skill, index) => (
                      <motion.div
                        key={`${skill.priority}-${skill.name}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.08 }}
                        className="bg-slate-700/25 rounded-2xl p-4 border border-slate-700/40"
                      >
                        <div className="flex items-center justify-between mb-3 gap-4">
                          <div>
                            <p className="text-white font-semibold">{skill.name}</p>
                            <p className="text-xs uppercase tracking-wide text-gray-400">
                              {skill.priority} • {skill.status === 'covered' ? 'profile matched' : skill.status === 'in_progress' ? 'roadmap in progress' : 'missing skill'}
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full border text-sm font-medium ${getScoreBadge(skill.score)}`}>
                            {skill.score}%
                          </span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
                          <motion.div
                            className={`h-full bg-gradient-to-r ${getScoreColor(skill.score)} rounded-full`}
                            initial={{ width: 0 }}
                            animate={{ width: `${skill.score}%` }}
                            transition={{ duration: 1 }}
                          />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-slate-700/20 rounded-2xl p-6 border border-slate-700/40 text-center">
                    <p className="text-white font-semibold mb-2">No skill scoring data yet</p>
                    <p className="text-gray-400 text-sm">
                      Add reviewed skills and an active role to unlock role-based skill scoring on the dashboard.
                    </p>
                  </div>
                )}
              </motion.section>
            </div>

            <div className="space-y-6">
              <MilestoneTracker />

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50"
              >
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <Bell className="w-5 h-5 text-purple-400 mr-2" />
                  Notifications
                  {notifications && notifications.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full text-xs">
                      {notifications.length}
                    </span>
                  )}
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {recentActivity.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Recent Activity</p>
                      <div className="space-y-2">
                        {recentActivity.slice(0, 2).map((item, index) => (
                          <motion.div
                            key={`activity-${index}`}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg"
                          >
                            <p className="text-white text-sm font-medium mb-1">{item.title}</p>
                            <p className="text-gray-400 text-xs">{item.description}</p>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}
                  {notifications && notifications.length > 0 ? (
                    notifications.slice(0, 5).map((notif, index) => (
                      <motion.div
                        key={notif.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="p-3 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors cursor-pointer"
                      >
                        <p className="text-white text-sm font-medium mb-1">{notif.title}</p>
                        <p className="text-gray-400 text-xs">{notif.message}</p>
                      </motion.div>
                    ))
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-4">No notifications</p>
                  )}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 }}
                className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50"
              >
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <Wrench className="w-5 h-5 text-rose-400 mr-2" />
                  Weak Skills
                </h3>
                {topWeakSkills.length > 0 ? (
                  <div className="space-y-3">
                    {topWeakSkills.map((skill, index) => (
                      <motion.div
                        key={skill.name}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 + index * 0.08 }}
                        className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-lg"
                      >
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <p className="text-white text-sm font-medium">{skill.name}</p>
                          <span className="text-rose-300 text-xs font-semibold">{skill.score}%</span>
                        </div>
                        <p className="text-rose-100/70 text-xs">
                          {skill.status === 'in_progress' ? 'Started in roadmap, but still needs stronger coverage.' : 'Missing from current profile and role coverage.'}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="p-3 bg-slate-700/30 rounded-lg">
                    <p className="text-white text-sm font-medium mb-1">No weak skills identified yet</p>
                    <p className="text-gray-400 text-xs">
                      The dashboard will highlight improvement areas once role-based skill mapping is available.
                    </p>
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50"
              >
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <Zap className="w-5 h-5 text-yellow-400 mr-2" />
                  Quick Actions
                </h3>
                <div className="space-y-2">
                  {[
                    { to: '/roadmap', icon: BookOpen, label: 'Roadmap', className: 'bg-purple-500/10 border-purple-500/20 text-purple-400' },
                    { to: '/profile', icon: User, label: 'Profile', className: 'bg-blue-500/10 border-blue-500/20 text-blue-400' },
                    { to: '/portfolio', icon: Award, label: 'Portfolio', className: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400' },
                    { to: '/settings', icon: Settings, label: 'Settings', className: 'bg-green-500/10 border-green-500/20 text-green-400' }
                  ].map((action, index) => (
                    <Link key={action.to} to={action.to}>
                      <motion.div
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 + index * 0.1 }}
                        whileHover={{ scale: 1.02, x: 5 }}
                        className={`flex items-center justify-between p-3 border rounded-lg hover:bg-slate-700/40 transition-all cursor-pointer ${action.className}`}
                      >
                        <div className="flex items-center">
                          <action.icon className="w-5 h-5 mr-2" />
                          <span className="text-white">{action.label}</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </motion.div>
                    </Link>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50"
              >
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <Brain className="w-5 h-5 text-blue-400 mr-2" />
                  AI Insights
                </h3>
                <div className="space-y-3">
                  <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
                    <p className="text-blue-400 text-xs mb-1">Performance</p>
                    <p className="text-white font-semibold capitalize">{performanceDisplay}</p>
                  </div>
                  <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/20">
                    <p className="text-green-400 text-xs mb-1">Pace</p>
                    <p className="text-white font-semibold capitalize">{paceDisplay}</p>
                  </div>
                  <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/20">
                    <p className="text-purple-400 text-xs mb-1">Next Best Action</p>
                    <p className="text-white font-semibold">{nextBestActionDisplay}</p>
                  </div>
                  {readinessComponents.length > 0 && (
                    <div className="pt-1">
                      <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Readiness Signals</p>
                      <div className="space-y-2">
                        {readinessComponents.slice(0, 3).map((component) => (
                          <div key={component.label} className="flex items-center justify-between text-sm bg-slate-700/25 rounded-lg px-3 py-2">
                            <span className="text-gray-300">{component.label}</span>
                            <span className="text-white font-semibold">{component.value}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </motion.main>
      </div>
      
      {/* AI Explainer Modal */}
      {explainerModalOpen && explainerJobs[explainerTab] && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setExplainerModalOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-4xl bg-slate-900 rounded-2xl overflow-hidden border border-slate-700/50"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/50">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-400" />
                AI Explainer: {explainerRoleTitle || 'No active role'}
              </h3>
              <button
                onClick={() => setExplainerModalOpen(false)}
                className="p-1.5 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            <div className="p-4">
              {explainerTab === 'video' ? (
                <video
                  controls
                  autoPlay
                  className="w-full rounded-xl bg-black"
                  src={`${PIPELINE_URL}/pipeline/video/download/${explainerJobs[explainerTab]}`}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-10 gap-4">
                  <Sparkles className="w-10 h-10 text-green-400 opacity-60" />
                  <audio
                    controls
                    autoPlay
                    className="w-full"
                    src={`${PIPELINE_URL}/pipeline/audio/download/${explainerJobs[explainerTab]}`}
                  />
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-slate-700/50 flex items-center justify-between">
              <p className="text-gray-400 text-sm capitalize">Model: {explainerModel}</p>
              <a
                href={`${PIPELINE_URL}/pipeline/${explainerTab}/download/${explainerJobs[explainerTab]}`}
                download
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition-colors flex items-center gap-2"
              >
                ⬇ Download
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Dashboard;
